import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AdminApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualData, setManualData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_prefix: '+39',
    phone: '',
    country: 'Italia',
    city: '',
    role_title: '',
    documents_accepted: true
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get('/admin/candidates');
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch candidates', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`/admin/candidates/${userId}/approve`);
      toast.success('Candidatura approvata! Votazione creata.');
      fetchApplications();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'approvazione'));
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.post(`/admin/candidates/${userId}/reject`);
      toast.success('Candidatura rifiutata');
      fetchApplications();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel rifiuto'));
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post('/admin/candidates/manual', manualData);
      toast.success('Candidato aggiunto manualmente');
      setDialogOpen(false);
      setManualData({
        first_name: '',
        last_name: '',
        email: '',
        phone_prefix: '+39',
        phone: '',
        country: 'Italia',
        city: '',
        role_title: '',
        documents_accepted: true
      });
      fetchApplications();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'inserimento manuale'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'In Attesa', color: 'bg-gray-200 text-gray-800' },
      approved_for_vote: { label: 'Approvato', color: 'bg-blue-200 text-blue-800' },
      voting: { label: 'In Votazione', color: 'bg-yellow-200 text-yellow-800' },
      accepted: { label: 'Accettato', color: 'bg-green-200 text-green-800' },
      rejected: { label: 'Rifiutato', color: 'bg-red-200 text-red-800' }
    };
    const badge = badges[status] || { label: status, color: 'bg-gray-200' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded ${badge.color}`}>{badge.label}</span>;
  };

  const filterByStatus = (status) => {
    return applications.filter(app => app.application_status === status);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-applications-page">
      <Navbar />

      <div className="container mx-auto px-6 pt-24 pb-12">
        <div className="flex items-center justify-between mb-12">
          <h1 className="text-4xl font-bold" data-testid="page-title">Gestione Candidature</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-gray-800" data-testid="add-manual-button">
                + Aggiungi Manualmente
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="manual-application-dialog">
              <DialogHeader>
                <DialogTitle>Aggiungi Candidato Manualmente</DialogTitle>
                <DialogDescription>Inserisci i dati del candidato senza upload di file</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Nome</Label>
                    <Input
                      id="first_name"
                      data-testid="manual-first-name-input"
                      required
                      value={manualData.first_name}
                      onChange={(e) => setManualData({...manualData, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Cognome</Label>
                    <Input
                      id="last_name"
                      data-testid="manual-last-name-input"
                      required
                      value={manualData.last_name}
                      onChange={(e) => setManualData({...manualData, last_name: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="manual-email-input"
                    type="email"
                    required
                    value={manualData.email}
                    onChange={(e) => setManualData({...manualData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefono (con prefisso)</Label>
                  <Input
                    id="phone"
                    data-testid="manual-phone-input"
                    required
                    value={manualData.phone}
                    onChange={(e) => setManualData({...manualData, phone: e.target.value})}
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Nazione</Label>
                  <Input
                    id="country"
                    data-testid="manual-country-input"
                    required
                    value={manualData.country}
                    onChange={(e) => setManualData({...manualData, country: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="city">Città</Label>
                  <Input
                    id="city"
                    data-testid="manual-city-input"
                    required
                    value={manualData.city}
                    onChange={(e) => setManualData({...manualData, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="role_title">Ruolo Lavorativo</Label>
                  <Input
                    id="role_title"
                    data-testid="manual-role-title-input"
                    required
                    value={manualData.role_title}
                    onChange={(e) => setManualData({...manualData, role_title: e.target.value})}
                  />
                </div>
                <Button type="submit" className="bg-black text-white hover:bg-gray-800 w-full" data-testid="submit-manual-button">
                  Aggiungi Candidato
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList>
            <TabsTrigger value="pending" data-testid="pending-tab">In Attesa ({filterByStatus('pending').length})</TabsTrigger>
            <TabsTrigger value="voting" data-testid="voting-tab">In Votazione ({filterByStatus('voting').length})</TabsTrigger>
            <TabsTrigger value="accepted" data-testid="accepted-tab">Accettati ({filterByStatus('accepted').length})</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="rejected-tab">Rifiutati ({filterByStatus('rejected').length})</TabsTrigger>
          </TabsList>

          {['pending', 'voting', 'accepted', 'rejected'].map((status) => (
            <TabsContent key={status} value={status}>
              {loading ? (
                <div className="text-center py-12">Caricamento...</div>
              ) : filterByStatus(status).length === 0 ? (
                <div className="text-center py-12 text-gray-600" data-testid="no-applications-message">
                  Nessuna candidatura in questo stato.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {filterByStatus(status).map((application) => (
                    <Card key={application.id} className="border border-gray-200" data-testid="application-card">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span>{application.first_name} {application.last_name}</span>
                          {getStatusBadge(application.status)}
                        </CardTitle>
                        <CardDescription>{application.email}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm mb-4">
                          <p><span className="font-semibold">Tel:</span> {application.phone_prefix || ''} {application.phone}</p>
                          <p><span className="font-semibold">Nazione:</span> {application.country || 'N/A'}</p>
                          <p><span className="font-semibold">Città:</span> {application.city}</p>
                          <p><span className="font-semibold">Ruolo:</span> {application.role_title}</p>
                          <p><span className="font-semibold">Data:</span> {new Date(application.created_at).toLocaleDateString('it-IT')}</p>
                        </div>
                        
                        {application.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleApprove(application.id)}
                              className="flex-1 bg-green-600 text-white hover:bg-green-700"
                              data-testid="approve-button"
                            >
                              Approva
                            </Button>
                            <Button
                              onClick={() => handleReject(application.id)}
                              variant="outline"
                              className="flex-1 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                              data-testid="reject-button"
                            >
                              Rifiuta
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
